import { formatCents, formatCentsBRL, Moeda, simboloMoeda } from "@/utils/money";

export {
  formatCents,
  formatCentsBRL,
  parseReaisToCents,
  centsToReaisInput,
  formatPercent,
  Moeda,
  MOEDAS,
  parseMoeda,
  simboloMoeda,
} from "@/utils/money";

/**
 * Dinheiro no banco vem em três unidades: centavos (verba, finance), reais
 * (controle_projetos.valor_projeto) e texto livre (pretensao_salarial). Todo
 * componente recebe a unidade explícita e converte aqui.
 */
export type MoneyUnit = "cents" | "reais";

export function toCents(value: number, unit: MoneyUnit): number {
  return unit === "cents" ? value : Math.round(value * 100);
}

const INPUT_FORMAT = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Texto de campo de dinheiro com milhar: "8.000,00" (parseReaisToCents lê de volta). */
export function formatCentsForInput(cents: number | null | undefined): string {
  if (cents == null) return "";
  return INPUT_FORMAT.format(cents / 100);
}

const COMPACT_THOUSANDS = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 });

/** "R$ 8 mil", "R$ 1,2 mi", "R$ 950". Para caber em card e resumo. */
export function formatCentsCompact(cents: number, moeda: Moeda = Moeda.BRL): string {
  const simbolo = simboloMoeda(moeda);
  const reais = cents / 100;
  if (Math.abs(reais) >= 1_000_000) return `${simbolo} ${COMPACT_THOUSANDS.format(reais / 1_000_000)} mi`;
  if (Math.abs(reais) >= 1_000) return `${simbolo} ${COMPACT_THOUSANDS.format(reais / 1_000)} mil`;
  return `${simbolo} ${COMPACT_THOUSANDS.format(reais)}`;
}

function compactNumberOnly(cents: number): { value: string; suffix: string } {
  const reais = cents / 100;
  if (Math.abs(reais) >= 1_000_000) return { value: COMPACT_THOUSANDS.format(reais / 1_000_000), suffix: " mi" };
  if (Math.abs(reais) >= 1_000) return { value: COMPACT_THOUSANDS.format(reais / 1_000), suffix: " mil" };
  return { value: COMPACT_THOUSANDS.format(reais), suffix: "" };
}

/**
 * Faixa de valor com uma regra só: "R$ 8 a 12 mil", "a partir de R$ 5 mil",
 * "até R$ 12 mil", "R$ 10 mil" (min = max). Sem nenhum dos dois, null.
 */
export function formatMoneyRange(
  min: number | null | undefined,
  max: number | null | undefined,
  { unit = "cents", compact = true, moeda = Moeda.BRL }: { unit?: MoneyUnit; compact?: boolean; moeda?: Moeda } = {},
): string | null {
  const minCents = min != null && min > 0 ? toCents(min, unit) : null;
  const maxCents = max != null && max > 0 ? toCents(max, unit) : null;
  const single = (cents: number) => (compact ? formatCentsCompact(cents, moeda) : formatCents(cents, moeda));

  if (minCents == null && maxCents == null) return null;
  if (minCents != null && maxCents == null) return `a partir de ${single(minCents)}`;
  if (minCents == null && maxCents != null) return `até ${single(maxCents)}`;
  if (minCents === maxCents) return single(minCents!);
  if (!compact) return `${formatCents(minCents!, moeda)} a ${formatCents(maxCents!, moeda)}`;

  const low = compactNumberOnly(minCents!);
  const high = compactNumberOnly(maxCents!);
  const simbolo = simboloMoeda(moeda);
  if (low.suffix === high.suffix) return `${simbolo} ${low.value} a ${high.value}${high.suffix}`;
  return `${simbolo} ${low.value}${low.suffix} a ${high.value}${high.suffix}`;
}

/**
 * Pretensão salarial é texto livre no banco ("5000", "R$ 8.000", "a combinar").
 * Número puro vira reais formatados; qualquer outro texto passa como veio,
 * sem prefixar "R$" de novo (bug do "R$ R$" nas recomendações).
 */
export function formatPretensao(raw: string | number | null | undefined): string | null {
  if (raw == null) return null;
  if (typeof raw === "number") return Number.isFinite(raw) ? formatCentsBRL(Math.round(raw * 100)) : null;
  const text = raw.trim();
  if (text === "") return null;
  if (/^\d{1,3}(\.\d{3})*(,\d{1,2})?$|^\d+([.,]\d{1,2})?$/.test(text)) {
    const normalized = text.includes(",") ? text.replace(/\./g, "").replace(",", ".") : text.replace(/\.(?=\d{3}\b)/g, "");
    const reais = Number(normalized);
    if (Number.isFinite(reais)) return formatCentsBRL(Math.round(reais * 100));
  }
  return text;
}
